import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { NativeSelect, Popover } from "@mantine/core";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Campaign, CampaignList as ListRow, CampaignOverlap, CampaignRun } from "../api/types";
import { campaignPath } from "../lib/campaign";
import { csvFilename, downloadCsv, toCsv } from "../lib/csv";
import { absent, orAbsent, shortDate } from "../lib/format";
import { PAGE_SIZE, applySorts, useListParams } from "../lib/useListParams";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { Stage } from "../ui/Stage";
import { FiltersPanel, Toolbar } from "../ui/Toolbar";
import { useRecordNav } from "../ui/RecordPage";
import { factorWord } from "../ui/words";
import { RecordLink } from "./RecordLink";
import { Spinner } from "./Spinner";
import { topFactors } from "./CampaignsTable";

/* A campaign's list: its newest full snapshot, one row per property in rank
 * order. A row opens the property's page, and its Back returns here with the
 * row marked. The people to call on a property are on that page.
 *
 * One list, where there were four: the builder's preview, the run, the
 * exports and the measurement. The line above the table says when the list
 * was taken and how many properties have joined or left since, and Update
 * the list takes a new one. An export takes the list first, so the file and
 * these rows are one ranking. A past list opens from History at ?list=.
 * docs/plans/campaigns-overhaul.md, The List tab. Rows from api.campaign_list
 * (engine migration 201).
 *
 * Another owner: when people at a property have gone out on another open
 * campaign that somebody else owns, the row says so and links to it, so the
 * owner can decide who calls. The export can leave those people out.
 * docs/plans/campaign-assignment.md, milestone 12. */
const FILTERS = ["state", "stage", "mobile"] as const;

const SELECT = "member_id,list_id,rank,score,breakdown,property_id,name,address_line1,city,state,property_type,unit_count,owner_company_id,owner_company_name,people_to_call,people_with_mobile,stage_word,stage_order";

export function CampaignList({ token, c, listId, onUpdate, updating }: {
  token: string;
  c: Campaign;
  /** The list on screen: the current one, or a past one from History. */
  listId: string | null;
  onUpdate: () => void;
  updating: boolean;
}) {
  const params = useListParams("rank", FILTERS, true, "campaign_list", "l.");
  const nav = useRecordNav();
  const location = useLocation();
  const [filtersOpen, setFiltersOpen] = useState(Object.keys(params.filters).length > 0);
  const past = Boolean(listId && c.list_id && listId !== c.list_id);
  const words = useStageWordsList(token);

  const pastList = useQuery({
    queryKey: ["campaign-list-run", listId],
    enabled: past,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign_run")
        .select("id,frozen_at,member_count,notes,run_by,job_id").eq("id", listId as string).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as CampaignRun | null;
    },
  });

  const narrowed = (opts?: { count: "exact"; head?: boolean }) => {
    let q = apiClient(token).from("campaign_list").select(SELECT, opts).eq("list_id", listId as string);
    const s = params.search.trim().replace(/[(),*"\\]/g, " ").trim();
    if (s) q = q.or(`name.ilike.*${s}*,address_line1.ilike.*${s}*,owner_company_name.ilike.*${s}*,city.ilike.*${s}*`);
    if (params.filters.state) q = q.eq("state", params.filters.state.toUpperCase());
    if (params.filters.stage) q = q.eq("stage_word", params.filters.stage);
    if (params.filters.mobile === "yes") q = q.gt("people_with_mobile", 0);
    return q;
  };

  const rows = useQuery({
    queryKey: ["campaign-list", listId, params.page, params.sorts, params.search, params.filters],
    enabled: Boolean(listId),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error } = await applySorts(narrowed(), params.sorts)
        .order("rank", { ascending: true }).range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ListRow[];
    },
  });
  const total = useQuery({
    queryKey: ["campaign-list", "count", listId, params.search, params.filters],
    enabled: Boolean(listId),
    queryFn: async () => {
      const { error, count } = await narrowed({ count: "exact", head: true });
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const scored = Object.keys((c.weights ?? {}) as object).length > 0;
  /* Whether the campaign's people have been chosen, which the list job does
     each time it makes the list (engine migration 233). A list made before
     then has none until it is updated. */
  const chosen = Boolean(c.people_taken_at);
  const list = rows.data ?? [];
  /* Every way into a property from this list says which campaign it came
     from, so its Who to call tab shows this campaign's picks first (James,
     2026-10-13). Until 2026-10-19 only the People to call figure did, and
     opening the row or its name showed the property's plain rule. */
  const fromCampaign = c.name ? `campaign=${encodeURIComponent(c.name)}` : undefined;

  /* People another owner has sent out on an open campaign, for the
     properties on this page (api.campaign_overlap, engine migration 217). It
     describes the campaigns as they are now, so a past list does not show it.
     Nothing is left out by it: the export asks. */
  const ids = list.map((r) => r.property_id).filter((x): x is string => Boolean(x));
  const overlap = useQuery({
    queryKey: ["campaign-overlap", c.name, ids],
    enabled: !past && ids.length > 0,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign_overlap")
        .select("property_id,words,other_campaign").eq("campaign", c.name ?? "").in("property_id", ids).limit(PAGE_SIZE);
      if (error) throw new Error(apiErrorMessage(error));
      return new Map(((data ?? []) as CampaignOverlap[]).map((o) => [o.property_id ?? "", o]));
    },
  });
  const n = total.data ?? null;
  const lastPage = n === null ? params.page : Math.max(1, Math.ceil(n / PAGE_SIZE));

  const columns: ColumnSpec<ListRow>[] = [
    { id: "rank", header: "#", role: "leading", fields: [], align: "num", sort: "rank", narrowLabel: null,
      cell: (r) => r.rank ?? absent() },
    ...(scored ? [{ id: "score", header: "Score", role: "leading", fields: [], align: "num", sort: "score", sortDescendingFirst: true, narrowLabel: null,
      cell: (r: ListRow) => <ScoreCell r={r} /> } as ColumnSpec<ListRow>] : []),
    { id: "name", header: "Property", role: "leading", fields: [], sort: "name", title: true, narrowLabel: null,
      cell: (r) => <RecordLink to="properties" id={r.property_id} label={r.name || r.address_line1 || "No name or street"} query={fromCampaign} /> },
    { id: "city", header: "City", role: "secondary", fields: [], sort: "city", narrowLabel: null, cell: (r) => orAbsent(r.city) },
    { id: "state", header: "State", role: "secondary", fields: [], sort: "state", narrowLabel: null, cell: (r) => orAbsent(r.state) },
    { id: "units", header: "Units", role: "secondary", fields: [], align: "num", sort: "unit_count", narrowLabel: "units",
      cell: (r) => r.unit_count?.toLocaleString() ?? absent() },
    { id: "stage", header: "Stage", role: "secondary", fields: [], sort: "stage_order", narrowLabel: null,
      cell: (r) => <Stage word={r.stage_word} order={r.stage_order ?? null} /> },
    { id: "owner", header: "Owner", role: "secondary", fields: [], sort: "owner_company_name", narrowLabel: null,
      cell: (r) => <RecordLink to="companies" id={r.owner_company_id} label={r.owner_company_name} /> },
    /* The people the campaign would call at this property, from its own
       rows (engine migration 233), one tap from the property page's Who to
       call with this campaign's picks first. A property with nobody
       connected has no rows and counts 0. Until 2026-10-23 these cells read
       "not taken" for it, because they tested the property's rows rather
       than whether the campaign's people were chosen. A dash only when they
       were not, and the line above the table says so. */
    { id: "people", header: "People to call", role: "detail", fields: [], align: "num", sort: "people_to_call", narrowLabel: "to call",
      cell: (r) => chosen
        ? <Link to={`/properties/${r.property_id}?tab=people&campaign=${encodeURIComponent(c.name ?? "")}`}>{(r.people_to_call ?? 0).toLocaleString()}</Link>
        : absent() },
    { id: "mobiles", header: "With a mobile", role: "secondary", fields: [], align: "num", sort: "people_with_mobile", narrowLabel: "with a mobile",
      cell: (r) => chosen ? (r.people_with_mobile ?? 0).toLocaleString() : absent() },
    ...(scored ? [{ id: "why", header: "Why", role: "detail", fields: [], narrowLabel: null,
      cell: (r: ListRow) => topFactors(r.breakdown) || absent() } as ColumnSpec<ListRow>] : []),
    ...(past ? [] : [{ id: "other", header: "Another owner", role: "secondary", fields: [], narrowLabel: null,
      cell: (r: ListRow) => {
        const o = overlap.data?.get(r.property_id ?? "");
        return o?.words
          ? <Link to={campaignPath(o.other_campaign ?? "")}>{o.words}</Link>
          : null;
      } } as ColumnSpec<ListRow>]),
  ];

  const chips = [
    ...(params.filters.state ? [{ label: `State: ${params.filters.state.toUpperCase()}`, onRemove: () => params.set({ filters: { ...params.filters, state: "" } }) }] : []),
    ...(params.filters.stage ? [{ label: `Stage: ${params.filters.stage}`, onRemove: () => params.set({ filters: { ...params.filters, stage: "" } }) }] : []),
    ...(params.filters.mobile ? [{ label: "Someone to call has a mobile", onRemove: () => params.set({ filters: { ...params.filters, mobile: "" } }) }] : []),
  ];

  if (!listId) {
    return (
      <section className="rp-tabbody" aria-label="List">
        <div className="cl-line">
          {c.list_job_status === "queued" || c.list_job_status === "running"
            ? <span><Spinner />Making the list.</span>
            : <span>No list yet.</span>}
          <Button kind="orange" onClick={onUpdate} disabled={updating || c.list_job_status === "queued" || c.list_job_status === "running"}>
            Make the list
          </Button>
        </div>
        {c.list_job_error ? <p className="refusal">The list was not made. {c.list_job_error}</p> : null}
      </section>
    );
  }

  return (
    <section className="rp-tabbody" aria-label="List">
      {past ? (
        <div className="cl-past" role="note">
          The list as of {shortDate(pastList.data?.frozen_at)}
          {pastList.data?.notes ? `. ${pastList.data.notes.replace(/\.$/, "")}` : ""}.{" "}
          <Link to={campaignPath(c.name ?? "")}>Current list</Link>
        </div>
      ) : (
        <div className="cl-line">
          <span>
            List as of {shortDate(c.list_at)}
            {c.list_note ? <span className="muted"> · {c.list_note.replace(/\.$/, "")}</span> : null}
            {(c.joined_since ?? 0) + (c.left_since ?? 0) > 0
              ? <> · <strong>{(c.joined_since ?? 0).toLocaleString()}</strong> joined and <strong>{(c.left_since ?? 0).toLocaleString()}</strong> left since</>
              : null}
            {/* Replaced, with the state kept, as a tab change is (RecordPage),
                so Back leaves the page and the back link keeps its words. */}
            {" · "}<Link to={{ search: "?tab=changes" }} replace state={location.state}>What changed</Link>
            {chosen ? null : <> · No people chosen yet. Update the list to choose them.</>}
            {c.list_behind && c.list_behind_words ? <> · {c.list_behind_words}</> : null}
          </span>
          {c.list_job_status === "queued" || c.list_job_status === "running"
            ? <span className="muted"><Spinner />Updating the list</span>
            : <Button onClick={onUpdate} disabled={updating}>Update the list</Button>}
        </div>
      )}
      {!past && c.list_job_error ? <p className="refusal">The last update did not finish. {c.list_job_error}</p> : null}

      <Toolbar
        id="list"
        search={{ label: "Search by property, owner or city", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        filtersCount={chips.length}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        chips={chips}
        onClearAll={() => params.set({ search: "", filters: {} })}
        right={list.length ? (
          <Button size="compact-sm" onClick={async () => {
            const { data, error } = await applySorts(narrowed(), params.sorts).order("rank", { ascending: true }).range(0, 4999);
            if (error) return;
            downloadCsv(csvFilename(`${c.name ?? "campaign"}-list`), toCsv(
              ["rank", "score", "name", "address_line1", "city", "state", "unit_count", "stage_word", "owner_company_name", "people_to_call", "people_with_mobile"],
              (data ?? []) as unknown as Record<string, unknown>[]));
          }}>CSV</Button>
        ) : null}
      />
      <FiltersPanel id="list" open={filtersOpen} onDone={() => setFiltersOpen(false)}>
        <div className="filters-own">
          <label className="cl-filter">
            <span className="field-label">State</span>
            <input defaultValue={params.filters.state ?? ""} key={params.filters.state ?? ""} maxLength={2} size={3}
                   onBlur={(e) => params.set({ filters: { ...params.filters, state: e.currentTarget.value.trim().toUpperCase() } })}
                   onKeyDown={(e) => { if (e.key === "Enter") params.set({ filters: { ...params.filters, state: e.currentTarget.value.trim().toUpperCase() } }); }} />
          </label>
          <NativeSelect label="Stage" value={params.filters.stage ?? ""}
                        data={[{ value: "", label: "Any" }, ...(words.data ?? []).map((w) => ({ value: w, label: w }))]}
                        onChange={(e) => params.set({ filters: { ...params.filters, stage: e.currentTarget.value } })} />
          <NativeSelect label="Mobiles among the people to call" value={params.filters.mobile ?? ""}
                        data={[{ value: "", label: "Any" }, { value: "yes", label: "At least one" }]}
                        onChange={(e) => params.set({ filters: { ...params.filters, mobile: e.currentTarget.value } })} />
        </div>
      </FiltersPanel>

      <p className="muted rp-count">
        {n === null ? <Spinner /> : `${n.toLocaleString()} ${n === 1 ? "property" : "properties"}.`}
        {rows.isFetching && !rows.isLoading ? <> <Spinner />Refreshing.</> : null}
        {!scored ? " Not ranked. Listed by unit count." : ""}
      </p>
      {overlap.isError ? (
        <p className="refusal" role="alert">
          Another owner's campaigns did not load, so this page does not show who else has sent these people out. {(overlap.error as Error).message}
        </p>
      ) : null}
      {rows.isError ? <p className="refusal" role="alert">{(rows.error as Error).message} <Button size="compact-sm" onClick={() => rows.refetch()}>Try again</Button></p>
        : rows.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : !list.length ? (
          <p className="muted">
            {params.search || chips.length
              ? <>No property on this list matches. <Button kind="link" size="compact-sm" onClick={() => params.set({ search: "", filters: {} })}>Clear</Button></>
              : <>No properties on this list. <Link to={campaignPath(c.name ?? "", "criteria")}>Criteria</Link></>}
          </p>
        ) : (
          <DataTable columns={columns} rows={list} rowKey={(r) => r.member_id ?? ""} params={params}
                     openId={nav?.lastOpened}
                     onOpen={(r) => { if (r.property_id) nav?.open(`/properties/${r.property_id}${fromCampaign ? `?${fromCampaign}` : ""}`, r.member_id); }} />
        )}
      {lastPage > 1 ? (
        <div className="bar cl-pager">
          <Button onClick={() => params.set({ page: params.page - 1 })} disabled={params.page <= 1}>Previous</Button>
          <span className="muted">Page {params.page.toLocaleString()} of {lastPage.toLocaleString()}</span>
          <Button onClick={() => params.set({ page: params.page + 1 })} disabled={params.page >= lastPage}>Next</Button>
        </div>
      ) : null}
    </section>
  );
}

/* The score, and on a click each factor's points, the scorecard Jesse asked
   for on 2026-09-25. Lead score breakdown (November) adds what is missing. */
function ScoreCell({ r }: { r: ListRow }) {
  if (r.score === null || r.score === undefined) return <>{absent()}</>;
  const parts = Object.entries((r.breakdown ?? {}) as Record<string, unknown>)
    .filter((e): e is [string, number] => typeof e[1] === "number" && e[1] > 0)
    .sort((a, b) => b[1] - a[1]);
  return (
    <Popover position="bottom-start" withinPortal>
      <Popover.Target>
        <button type="button" className="cl-score" aria-label={`Score ${Math.round(Number(r.score))}, what makes it up`}>
          {Math.round(Number(r.score))}
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <table className="cl-scorecard">
          <tbody>
            {parts.map(([k, v]) => (
              <tr key={k}><td>{factorWord(k)}</td><td className="num">{v.toFixed(1)}</td></tr>
            ))}
            <tr className="cl-scorecard-total"><td>Score</td><td className="num">{Number(r.score).toFixed(1)}</td></tr>
          </tbody>
        </table>
      </Popover.Dropdown>
    </Popover>
  );
}

/** The team's six stage words, for the Stage filter. */
function useStageWordsList(token: string) {
  return useQuery({
    queryKey: ["stage-word-list"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("stage_word").select("word,word_order").order("word_order").limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      const seen: string[] = [];
      for (const r of (data ?? []) as { word: string | null }[]) if (r.word && !seen.includes(r.word)) seen.push(r.word);
      return seen;
    },
  });
}

